import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, addDoc, getDocs, query, where, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';

const CREATOR_EMAIL = 'raouf.ia2012@gmail.com';

const YT_PARAMS = 'rel=0&modestbranding=1&iv_load_policy=3&playsinline=1&enablejsapi=1&disablekb=1&cc_load_policy=0';

function getEmbedUrl(url) {
  if (!url) return null;
  const u = url.trim();
  const yt  = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt)  return { type: 'youtube',   embed: `https://www.youtube-nocookie.com/embed/${yt[1]}?${YT_PARAMS}` };
  const yts = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (yts) return { type: 'youtube',   embed: `https://www.youtube-nocookie.com/embed/${yts[1]}?${YT_PARAMS}` };
  const tt  = u.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (tt)  return { type: 'tiktok',    embed: `https://www.tiktok.com/embed/v2/${tt[1]}` };
  const ig  = u.match(/instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/);
  if (ig)  return { type: 'instagram', embed: `https://www.instagram.com/p/${ig[1]}/embed/` };
  return { type: 'unknown', url: u };
}

export default function VideosPage() {
  const { user } = useAuth();
  const isCreator = user?.email === CREATOR_EMAIL;

  const [videos,   setVideos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [link,     setLink]     = useState('');
  const [caption,  setCaption]  = useState('');
  const [adding,   setAdding]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error,    setError]    = useState('');

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'posts'),
        where('type', '==', 'video')
      ));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setVideos(docs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVideos(); }, []);

  const handleAdd = async () => {
    setError('');
    const parsed = getEmbedUrl(link);
    if (!parsed || parsed.type === 'unknown') { setError('Invalid link. Use YouTube, TikTok or Instagram.'); return; }
    setAdding(true);
    try {
      await addDoc(collection(db, 'posts'), {
        type: 'video', videoUrl: link.trim(), caption: caption.trim(),
        embedUrl: parsed.embed, platform: parsed.type,
        uid: user.uid, userId: user.uid,
        displayName: user.displayName || '', username: user.displayName || '',
        photoURL: user.photoURL || '',
        likes: 0, likedBy: [], viewCount: 0,
        createdAt: serverTimestamp(),
      });
      setLink(''); setCaption(''); setShowForm(false);
      await fetchVideos();
    } catch { setError('Error saving. Try again.'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try { await deleteDoc(doc(db, 'posts', id)); setVideos(v => v.filter(x => x.id !== id)); } catch {}
  };

  return (
    <div className="videos-root" style={s.root}>

      {/* Top bar */}
      <div style={s.topBar}>
        <span style={s.topTitle}>Videos</span>
        {isCreator && (
          <button style={s.addBtn} onClick={() => setShowForm(f => !f)}>
            {showForm ? '✕' : '+'}
          </button>
        )}
      </div>

      {/* Add form */}
      {isCreator && showForm && (
        <div style={s.formOverlay}>
          <div style={s.form}>
            <p style={s.formTitle}>📎 Add a video</p>
            <p style={s.formHint}>YouTube · TikTok · Instagram</p>
            <input
              style={s.input}
              placeholder="https://www.tiktok.com/@user/video/123…"
              value={link}
              onChange={e => { setLink(e.target.value); setError(''); }}
            />
            <input
              style={{ ...s.input, marginTop: 8 }}
              placeholder="Caption (optional)"
              value={caption}
              onChange={e => setCaption(e.target.value)}
            />
            {error && <p style={s.error}>{error}</p>}
            <button
              style={{ ...s.publishBtn, opacity: adding || !link ? 0.6 : 1 }}
              onClick={handleAdd}
              disabled={adding || !link}
            >
              {adding ? 'Saving…' : '✓ Publish'}
            </button>
            <button style={s.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div style={s.loader}>
          <div style={s.spinner} />
        </div>
      ) : videos.length === 0 ? (
        <div style={s.empty}>
          <span style={{ fontSize: 52, opacity: 0.25 }}>🎬</span>
          <p style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginTop: 12 }}>No videos yet</p>
        </div>
      ) : (
        <div style={s.feed}>
          {videos.map(v => (
            <VideoSlide
              key={v.id}
              video={v}
              isCreator={isCreator}
              onDelete={() => handleDelete(v.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── One full-screen video slide ── */
function VideoSlide({ video, isCreator, onDelete }) {
  const [active, setActive] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const slideRef = useRef(null);

  const rawUrl   = video.videoUrl || video.url || '';
  const parsed   = video.embedUrl
    ? { embed: video.embedUrl, type: video.platform || video.type }
    : getEmbedUrl(rawUrl);
  const platform = (parsed?.type || '').toLowerCase();
  const hasEmbed = ['youtube', 'tiktok', 'instagram'].includes(platform) && parsed?.embed;

  /* Show iframe when in view, destroy when out */
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        setActive(entry.isIntersecting);
        if (!entry.isIntersecting) setLoaded(false);
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const embedSrc = useMemo(() => {
    if (!parsed?.embed) return '';
    if (platform === 'youtube') {
      // Always use nocookie domain to avoid consent popup and reduce ads
      const src = parsed.embed.replace('www.youtube.com', 'www.youtube-nocookie.com');
      return src + '&autoplay=1';
    }
    return parsed.embed;
  }, [parsed?.embed, platform]); // eslint-disable-line

  return (
    <div ref={slideRef} className="video-slide">

      {hasEmbed ? (
        <div className="video-player-wrap">
          {active && (
            <>
              {!loaded && <div style={vs.loading}><div style={vs.spinnerSm} /></div>}
              <iframe
                key={embedSrc}
                src={embedSrc}
                style={{ ...vs.iframe, opacity: loaded ? 1 : 0 }}
                title={video.caption || 'Video'}
                frameBorder="0"
                scrolling="no"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={() => setLoaded(true)}
              />
            </>
          )}
          {platform === 'tiktok' && <div style={vs.maskTop} />}
          {platform === 'tiktok' && <div style={vs.maskBottom} />}
        </div>
      ) : (
        /* ── Fallback card ── */
        <div style={vs.fallback}>
          <span style={{ fontSize: 56, marginBottom: 16 }}>🎬</span>
          <p style={vs.fallTitle}>{video.caption || 'Video'}</p>
          <a href={rawUrl} target="_blank" rel="noopener noreferrer" style={vs.watchBtn}>
            Open video →
          </a>
        </div>
      )}

      {/* Bottom gradient info */}
      <div style={vs.info}>
        {video.caption && <p style={vs.caption}>{video.caption}</p>}
        <div style={vs.row}>
          {platform && (
            <span style={{
              ...vs.tag,
              background: platform === 'tiktok' ? '#000' : platform === 'youtube' ? '#FF0000' : '#E1306C'
            }}>
              {platform.toUpperCase()}
            </span>
          )}
          {video.username && <span style={vs.author}>@{video.username}</span>}
          <div style={{ flex: 1 }} />
          <a href={rawUrl} target="_blank" rel="noopener noreferrer" style={vs.openBtn}>🔗</a>
          {isCreator && (
            <button style={vs.delBtn} onClick={onDelete}>🗑</button>
          )}
        </div>
      </div>

    </div>
  );
}

/* ── Styles ── */
const s = {
  root:        { background: '#F0F2F5' },
  topBar:      { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' },
  topTitle:    { color: '#1C1E21', fontSize: 18, fontWeight: 800 },
  addBtn:      { background: '#1877F2', color: '#fff', border: 'none', borderRadius: 99, width: 36, height: 36, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  formOverlay: { position: 'absolute', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  form:        { background: '#fff', borderRadius: 20, padding: '24px 20px', width: '100%', maxWidth: 400 },
  formTitle:   { fontSize: 16, fontWeight: 800, color: '#1C1E21', marginBottom: 4 },
  formHint:    { fontSize: 12, color: '#8A8D91', marginBottom: 14 },
  input:       { width: '100%', border: '1.5px solid #E4E6EA', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#1C1E21', outline: 'none', fontFamily: 'inherit', background: '#F0F2F5', boxSizing: 'border-box' },
  error:       { color: '#e74c3c', fontSize: 12, marginTop: 6 },
  publishBtn:  { marginTop: 14, width: '100%', background: '#1877F2', color: '#fff', border: 'none', borderRadius: 10, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  cancelBtn:   { marginTop: 8, width: '100%', background: 'none', color: '#8A8D91', border: 'none', padding: '10px', fontSize: 13, cursor: 'pointer' },

  feed:        { position: 'absolute', inset: 0, overflowY: 'scroll', scrollSnapType: 'y mandatory', scrollbarWidth: 'none' },
  loader:      { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner:     { width: 40, height: 40, border: '3px solid rgba(255,255,255,0.15)', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty:       { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
};

const vs = {
  loading:     { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 },
  spinnerSm:   { width: 32, height: 32, border: '3px solid rgba(255,255,255,0.15)', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  iframe:      { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', transition: 'opacity 0.3s' },

  /* TikTok masks: hide top bar + bottom related videos */
  maskTop:     { position: 'absolute', top: 0, left: 0, right: 0, height: '8%', background: '#000', zIndex: 10, pointerEvents: 'none' },
  maskBottom:  { position: 'absolute', bottom: 0, left: 0, right: 0, height: '22%', background: '#000', zIndex: 10, pointerEvents: 'none' },

  fallback:    { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 },
  fallTitle:   { color: '#fff', fontSize: 18, fontWeight: 600, textAlign: 'center', marginBottom: 20 },
  watchBtn:    { background: '#fff', color: '#000', borderRadius: 99, padding: '13px 28px', fontSize: 15, fontWeight: 700, textDecoration: 'none' },

  info:        { position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20, padding: '12px 16px 16px', background: '#fff', borderTop: '1px solid #E4E6EA', pointerEvents: 'none' },
  caption:     { color: '#1C1E21', fontSize: 14, fontWeight: 600, marginBottom: 8, lineHeight: 1.4 },
  row:         { display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'all' },
  tag:         { fontSize: 10, fontWeight: 800, color: '#fff', borderRadius: 6, padding: '2px 8px', letterSpacing: '0.5px' },
  author:      { fontSize: 13, color: '#65676B', fontWeight: 500 },
  openBtn:     { background: '#F0F2F5', border: 'none', borderRadius: 99, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, textDecoration: 'none', color: '#1C1E21' },
  delBtn:      { background: 'rgba(250,62,62,0.15)', border: 'none', borderRadius: 99, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15, color: '#e74c3c' },
};
